<script setup>
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Textarea from "primevue/textarea";
import Select from "primevue/select";
import RadioButton from "primevue/radiobutton";
import Checkbox from "primevue/checkbox";
import InputMask from "primevue/inputmask";
import Button from "primevue/button";

import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';

const model = defineModel();
defineProps({
    type: String,
    options: Array,
    label: String,
    textColor: String,
    mask: String,
    rows: [Number, String],
    invalid: String,
    required: Boolean,
    currency: String,
    hideLabel: Boolean,
    placeholder: String,
})
</script>

<template>
    <div v-if="label" class="flex flex-col gap-2">
        <label v-if="!hideLabel" for="name" :class="textColor" class="font-medium text-sm">{{ label }} <span v-if="required" class="text-red-500">*</span></label>
        <InputText v-if="type === 'text'" :invalid="!!invalid" v-model="model"/>
        <vue-tel-input v-if="type === 'phone'" :invalidMsg="$t('widget.invalid_phone')" mode="international" :preferredCountries="['CZ','SK', 'DE', 'PL', 'AT']" :inputOptions="{
            placeholder: '',
            showDialCode: false,
        }" v-model="model"></vue-tel-input>
        <InputNumber v-if="type === 'number'" :invalid="!!invalid" v-model="model" buttonLayout="horizontal">
            <template #incrementbuttonicon>
                <span class="pi pi-plus"/>
            </template>
            <template #decrementbuttonicon>
                <span class="pi pi-minus"/>
            </template>
        </InputNumber>
        <InputMask v-if="type === 'mask'" :invalid="!!invalid" v-model="model" :mask="mask"/>
        <InputNumber v-if="type === 'price'" v-model="model" class="w-full" :min="0" mode="currency"
                     :currency="currency" :locale="$page.props.locale ?? 'cs-CZ'"/>
        <Textarea v-if="type === 'textarea'" :placeholder="placeholder" :invalid="!!invalid" v-model="model" :rows="Number(rows)"></Textarea>
        <Select v-if="type === 'list'" :invalid="!!invalid" option-value="label" option-label="label" v-model="model" :options="options"></Select>
        <Select v-if="type === 'select'" :options="options" v-model="model" optionValue="value"
                optionLabel="label"></Select>
        <div v-if="type === 'radio'" class="flex flex-col gap-2 mb-2">
            <template v-for="(option, i) in options" :key="i+'-'+option.label">
                <div v-if="option.label" class="flex items-center gap-2">
                    <RadioButton v-model="model" :input-id="i+'-'+option.label" :name="label" :value="option.label" />
                    <label>{{ option.label }}</label>
                </div>
            </template>
        </div>
        <div v-if="type === 'checkbox'" class="flex flex-col gap-2 mb-1">
            <template v-for="option in options" :key="option">
                <div v-if="option.label" class="flex items-center gap-2">
                    <Checkbox v-model="model" :value="option.label" />
                    <label>{{ option.label }}</label>
                </div>
            </template>
        </div>
        <small v-if="!!invalid" class="text-red-500">{{invalid}}</small>
    </div>
</template>

<style scoped lang="scss">
:deep(.vue-tel-input) {
    font-family: inherit;
    font-feature-settings: inherit;
    font-size: 1rem;
    color: var(--inputtext-color);
    background: var(--inputtext-background);
    padding-block: calc(var(--inputtext-padding-y) - 5px);
    padding-inline: 3px;
    border: 1px solid var(--inputtext-border-color);
    transition: background var(--inputtext-transition-duration),color var(--inputtext-transition-duration),border-color var(--inputtext-transition-duration),outline-color var(--inputtext-transition-duration),box-shadow var(--inputtext-transition-duration);
    appearance: none;
    border-radius: var(--inputtext-border-radius);
    outline-color: transparent;
    box-shadow: var(--inputtext-shadow);
    &:enabled:focus {
        border-color: var(--inputtext-focus-border-color);
        box-shadow: var(--inputtext-focus-ring-shadow);
        outline: var(--inputtext-focus-ring-width) var(--inputtext-focus-ring-style) var(--inputtext-focus-ring-color);
        outline-offset: var(--inputtext-focus-ring-offset);
    }
    &:enabled:hover {
        border-color: var(--inputtext-hover-border-color);
    }
    input {
        background-color: var(--inputtext-background)!important;
    }
}
</style>
